// Event capacity is a deliberate host choice (GIS-266).
//
// It used to be a silent 100 that nothing enforced. Now the rules refuse a
// guest's own RSVP past it, checkout refuses a ticket, and both apps swap Join
// for "This event is full." So a new event has no capacity until the host
// types one in, and saving without one is refused.
//
// The saved shape is unchanged: `size.sitters`, plus `size.movers` for the
// second group on a two-group event. Anything that is not a positive number
// counts as no cap everywhere else (backend src/utils/eventCapacity.js, the
// apps), which is how events saved before this keep working. Editing one of
// those is allowed without setting a capacity; a value typed in has to make
// sense either way.

const sidesFor = (event) => (event?.groupCount === 2 ? ["sitters", "movers"] : ["sitters"]);

const isBlank = (value) => value == null || value === "";

/**
 * Capacity errors for the save path, in the same plain style as the other
 * form errors.
 *
 * @param {Object} event form state
 * @param {boolean} isEditMode true when saving an event that already exists
 * @returns {string[]}
 */
export const capacityErrors = (event, isEditMode = false) => {
  const sides = sidesFor(event);
  const twoGroups = sides.length === 2;
  const values = sides.map((side) => event?.size?.[side]);

  const missing = values.some(isBlank);
  if (missing && !isEditMode) {
    return [twoGroups ? "Capacity is required for both groups" : "Capacity is required"];
  }

  const invalid = values.some((value) => {
    if (isBlank(value)) return false;
    const n = Number(value);
    if (!Number.isInteger(n)) return true;
    // An existing event may keep 0, which has always meant no cap.
    return isEditMode ? n < 0 : n < 1;
  });
  if (invalid) {
    return [
      twoGroups
        ? "Capacity must be a whole number above 0 for both groups"
        : "Capacity must be a whole number above 0",
    ];
  }

  return [];
};

/**
 * The number to store for a capacity field: a number, or null while it is
 * empty. The input hands over a string.
 *
 * @param {string} raw
 * @returns {number|null}
 */
export const parseCapacityInput = (raw) => {
  if (raw == null || String(raw).trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};
